import { useState } from 'react';
import type { CommsChannel, CommsKind, CommsMessage, CommsMessageStatus } from '@salonomy/types';
import { Button, Card, EmptyState, Field, Pill, Select, Spinner, TextInput } from '@salonomy/ui';
import type { PillTone } from '@salonomy/ui';
import { useCommsMessages, MESSAGES_PAGE } from '../../data/comms.js';
import { money } from '../../lib/format.js';

/**
 * WS8 — the per-message audit log. A paged table over GET /comms/messages with channel/kind/client
 * filters forwarded to the API. Recipients are REDACTED by the API (toRedacted) — we render exactly
 * what it returns and never try to un-mask. Every cost is the server's costPence. This is the
 * "every message is explainable" surface that makes the comms line on an invoice auditable.
 */

const CHANNEL_FILTERS: Array<{ value: '' | CommsChannel; label: string }> = [
  { value: '', label: 'All channels' },
  { value: 'email', label: 'Email' },
  { value: 'push', label: 'Push' },
  { value: 'sms', label: 'SMS' },
  { value: 'whatsapp', label: 'WhatsApp' },
];

const KIND_FILTERS: Array<{ value: '' | CommsKind; label: string }> = [
  { value: '', label: 'All kinds' },
  { value: 'reminder', label: 'Reminders' },
  { value: 'campaign', label: 'Campaigns' },
  { value: 'gap_offer', label: 'Gap offers' },
  { value: 'transactional', label: 'Transactional' },
  { value: 'manual', label: 'Manual' },
];

const STATUS_TONE: Record<CommsMessageStatus, PillTone> = {
  queued: 'soft',
  sent: 'cobalt',
  delivered: 'success',
  failed: 'danger',
  paused_at_cap: 'danger',
  skipped_no_consent: 'neutral',
  skipped_no_contact: 'neutral',
};

const STATUS_LABEL: Record<CommsMessageStatus, string> = {
  queued: 'Queued',
  sent: 'Sent',
  delivered: 'Delivered',
  failed: 'Failed',
  paused_at_cap: 'Paused at cap',
  skipped_no_consent: 'Skipped — no consent',
  skipped_no_contact: 'Skipped — no contact',
};

export function MessageHistory() {
  const [channel, setChannel] = useState<'' | CommsChannel>('');
  const [kind, setKind] = useState<'' | CommsKind>('');
  const [clientId, setClientId] = useState('');
  const [offset, setOffset] = useState(0);

  const filter = {
    channel: channel || undefined,
    kind: kind || undefined,
    clientId: clientId.trim() || undefined,
  };

  const { data, isLoading, isError, isFetching } = useCommsMessages(filter, offset);
  const rows = data?.data ?? [];
  const total = data?.pagination.total ?? 0;
  const limit = data?.pagination.limit ?? MESSAGES_PAGE;

  function resetPaging<T>(setter: (v: T) => void) {
    return (v: T) => {
      setter(v);
      setOffset(0);
    };
  }

  return (
    <Card>
      <div className="section-title" style={{ marginBottom: 2 }}>
        Message history
      </div>
      <div className="muted" style={{ fontSize: 13, marginBottom: 14 }}>
        Every message sent — reminders, campaigns and one-offs — with its channel, status and cost.
        Recipients are masked for privacy.
      </div>

      <div className="comms-filters">
        <Field label="Channel">
          <Select
            value={channel}
            onChange={(e) => resetPaging(setChannel)(e.target.value as '' | CommsChannel)}
          >
            {CHANNEL_FILTERS.map((f) => (
              <option key={f.value} value={f.value}>
                {f.label}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Kind">
          <Select
            value={kind}
            onChange={(e) => resetPaging(setKind)(e.target.value as '' | CommsKind)}
          >
            {KIND_FILTERS.map((f) => (
              <option key={f.value} value={f.value}>
                {f.label}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Client ID" hint="Optional — filter to one client">
          <TextInput
            value={clientId}
            placeholder="client UUID"
            onChange={(e) => resetPaging(setClientId)(e.target.value)}
          />
        </Field>
      </div>

      {isLoading ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '16px 0' }}>
          <Spinner size={16} />
          <span className="muted" style={{ fontSize: 13 }}>
            Loading messages…
          </span>
        </div>
      ) : isError ? (
        <div className="muted" style={{ fontSize: 13, padding: '8px 0' }}>
          Couldn&rsquo;t load the message log. It&rsquo;s read live — try again in a moment.
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          title="No messages yet"
          description="When you send a campaign, schedule reminders or message a client, every send is logged here."
        />
      ) : (
        <>
          <div className="comms-table">
            {rows.map((m) => (
              <MessageRow key={m.id} message={m} />
            ))}
          </div>

          {total > limit ? (
            <div className="comms-pager">
              <Button
                variant="outline"
                size="sm"
                disabled={offset === 0 || isFetching}
                onClick={() => setOffset((o) => Math.max(0, o - limit))}
              >
                ← Newer
              </Button>
              <span className="muted" style={{ fontSize: 12.5 }}>
                {offset + 1}–{Math.min(offset + limit, total)} of {total}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={offset + limit >= total || isFetching}
                onClick={() => setOffset((o) => o + limit)}
              >
                Older →
              </Button>
            </div>
          ) : null}
        </>
      )}
    </Card>
  );
}

function MessageRow({ message }: { message: CommsMessage }) {
  const routed =
    message.routedFromChannel && message.routedFromChannel !== message.channel
      ? `${message.routedFromChannel} → ${message.channel}`
      : message.channel;
  const when = message.sentAt ?? message.createdAt;
  return (
    <div className="comms-row" role="row">
      <div style={{ minWidth: 0 }}>
        {/* The API redacts recipients — render toRedacted verbatim, never un-mask. */}
        <div className="comms-row__to">{message.toRedacted}</div>
        <div className="comms-row__sub">
          {KIND_TEXT[message.kind]} · {routed}
          {message.subject ? ` · ${message.subject}` : ''} · {shortDateTime(when)}
        </div>
      </div>
      <Pill tone={STATUS_TONE[message.status]}>{STATUS_LABEL[message.status]}</Pill>
      <span className="comms-row__cost">
        {message.metered && message.costPence > 0 ? money(message.costPence) : 'Free'}
      </span>
    </div>
  );
}

const KIND_TEXT: Record<CommsKind, string> = {
  reminder: 'Reminder',
  campaign: 'Campaign',
  gap_offer: 'Gap offer',
  transactional: 'Transactional',
  manual: 'Manual',
};

/** ISO instant → a compact local date+time for the audit row (display only). */
function shortDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}
